import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { AspectRatioInput } from './ui-aspect.type';
import styles from './ui-aspect.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAspect
 * @element ui-aspect
 * @classdesc Represents a class for <code>ui-aspect</code> element.
 * ui-aspect should have a nested child element to work correctly.
 * @property {string} ratio {@attr ratio} Aspect ratio in format M:N.
 * @property {boolean} centered {@attr centered} Sets the first child to centered relatively
 * ui-aspect container.
 * @slot
 * @example
 * <ui-aspect ratio="16:9">
 *   <div>Some first child element.</div>
 * </ui-aspect>
 */
@Component({
    tag: 'ui-aspect',
    styles,
})
export class UIAspect extends UIElement {
    /**
     * Regex used to validate aspect ratio strings.
     * Expected format: "width:height", where width and height are integers or decimals.
     * Examples: "16:9", "4:3", "1.5:1", "2:0.75"
     */
    private static readonly AspectRatioRegex: RegExp =
        /^([0-9]*[.])?[0-9]+:([0-9]*[.])?[0-9]+$/;

    @Attribute(String) ratio!: AspectRatioInput;
    @Attribute(Boolean) centered?: boolean;

    /**
     * Parses an aspect ratio string (e.g. "16:9") into a numeric tuple.
     * @param {string} ratio
     * @returns {Array<number>}
     */
    private static parseAspectRatio(ratio: AspectRatioInput): [number, number] {
        const [width, height] = ratio.split(':').map((x) => Number(x.trim()));
        return [width, height];
    }

    /**
     * Applies a visual aspect ratio to an element. Uses native CSS `aspect-ratio` when supported,
     * and falls back to the padding-top technique for older browsers.
     * @param {HTMLElement} element
     * @param {string} ratio
     */
    static applyAspectRatio(
        element: HTMLElement,
        ratio: AspectRatioInput
    ): void {
        if (!this.AspectRatioRegex.test(ratio)) {
            console.error(`Aspect ratio attribute should be in format W:H.`);
            return;
        }

        const [width, height] = this.parseAspectRatio(ratio);
        if (CSS?.supports('aspect-ratio: 1 / 1')) {
            element.style.aspectRatio = `${width} / ${height}`;
        } else {
            const percentage = (height / width) * 100;
            if (Number.isFinite(percentage)) {
                element.style.paddingTop = `${percentage}%`;
            } else {
                console.error(`Aspect ratio: division by zero.`);
            }
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        UIAspect.applyAspectRatio(this, this.ratio);
    }
}
